import React, { useState } from 'react';
import { ShipmentDetail, ShipmentHistoryItem, ShipmentStatus } from '../../types/shipment.types';
import { Campaign } from '../../types/campaign.types';
import ShipmentStatusBadge from './ShipmentStatusBadge';
import LocationMap from './LocationMap';
import { formatDate } from '../../utils/dateFormatter';
import { Button } from '../../components/Button'; // Import standardized Button component

interface ShipmentDetailsSideNavProps {
  isOpen: boolean;
  onClose: () => void;
  shipmentDetails: ShipmentDetail | null;
  onEdit?: (updatedShipment: Partial<ShipmentDetail>) => void;
  isLoading?: boolean;
  campaigns?: Campaign[];
  loadingCampaigns?: boolean;
  campaignError?: string | null;
  shipmentHistory?: ShipmentHistoryItem[];
  historyLoading?: boolean;
}

/**
 * Side panel component for viewing and editing shipment details
 * Includes tabs for Details and History
 */
const ShipmentDetailsSideNav: React.FC<ShipmentDetailsSideNavProps> = ({
  isOpen,
  onClose,
  shipmentDetails,
  onEdit,
  isLoading = false,
  campaigns = [],
  loadingCampaigns = false,
  campaignError = null,
  shipmentHistory = [],
  historyLoading = false
}) => {
  // Tab state
  const [activeTab, setActiveTab] = useState<'details' | 'history'>('details');
  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  // Form state for edit mode
  const [editableShipment, setEditableShipment] = useState<Partial<ShipmentDetail>>({});

  // Don't render if not open
  if (!isOpen) return null;


  // If no shipment data at all, show global loading state
  if (!shipmentDetails) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900 dark:border-gray-100"></div>
          <div className="mt-4 text-center dark:text-gray-100">Loading shipment data...</div>
        </div>
      </div>
    );
  }

  // Function to handle edit button click
  const handleEditClick = () => {
    if (!editMode) {
      // Enter edit mode - initialize form state with current details
      setEditableShipment({...shipmentDetails});
      setEditMode(true);
    } else {
      // Exit edit mode without saving
      setEditMode(false);
    }
  };

  // Function to handle save click
  const handleSaveClick = () => {
    if (onEdit) {
      // Don't exit edit mode immediately if there's a potential campaign change
      // This allows the loading indicator to display while update is in progress
      if (editableShipment.campaignId !== shipmentDetails?.campaignId) {
        // Exit edit mode to show loading state
        setEditMode(false);
      }
      onEdit(editableShipment);
    } else {
      // If no edit handler, just exit edit mode
      setEditMode(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end bg-black bg-opacity-30">
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-lg w-full max-w-2xl h-full overflow-y-auto">
        {/* Sticky Header */}
        <div className="sticky top-0 bg-white dark:bg-slate-900 border-b dark:border-white/10 z-10">
          {/* Header Content */}
          <div className="flex items-center px-6 py-4">
            <button
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-2xl mr-4"
              onClick={onClose}
              aria-label="Close"
            >
              &times;
            </button>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex-1">{shipmentDetails?.name || 'Shipment Details'}</h2>
            <ShipmentStatusBadge status={shipmentDetails?.status || ShipmentStatus.CREATED} />
          </div>

          {/* Tabs */}
          <div className="border-b dark:border-white/10">
            <div className="flex">
              <button
                className={`flex-1 py-2 text-center ${
                  activeTab === 'details' ? 'border-b-2 border-primary-100 font-bold dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'
                }`}
                onClick={() => setActiveTab('details')}
              >
                Details
              </button>
              <button
                className={`flex-1 py-2 text-center ${
                  activeTab === 'history' ? 'border-b-2 border-primary-100 font-bold dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'
                }`}
                onClick={() => setActiveTab('history')}
              >
                History
              </button>
            </div>
          </div>
        </div>

        {/* Content area - changes based on active tab */}
        <div className="px-6 py-4">
          {activeTab === 'details' && (
            <div>
              {/* Edit/Save buttons */}
              {editMode ? (
                <div className="flex justify-end mb-4">
                  <Button
                    variant="tertiary"
                    onClick={() => setEditMode(false)}
                    className="mr-2"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSaveClick}
                  >
                    Save
                  </Button>
                </div>
              ) : (
                <div className="flex justify-end mb-4">
                  <Button
                    variant="primary"
                    onClick={handleEditClick}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Loading...' : 'Edit'}
                  </Button>
                </div>
              )}

              {/* Display different content based on edit mode */}
              {editMode ? (
                <ShipmentDetailsEditForm
                  shipment={editableShipment}
                  onChange={setEditableShipment}
                  campaigns={campaigns}
                  loadingCampaigns={loadingCampaigns}
                  campaignError={campaignError}
                />
              ) : isLoading ? (
                <div className="flex flex-col justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900 dark:border-gray-100 mb-3"></div>
                  <p className="text-gray-600 dark:text-gray-400">Updating shipment...</p>
                </div>
              ) : (
                <ShipmentDetailsView shipment={shipmentDetails} />
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <ShipmentHistoryView history={shipmentHistory} isLoading={historyLoading} />
          )}
        </div>
      </div>
    </div>
  );
};

// Component to display shipment details in read-only mode
const ShipmentDetailsView: React.FC<{shipment: ShipmentDetail | null}> = ({ shipment }) => {
  if (!shipment) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900 dark:border-gray-100 mb-4"></div>
        <p className="dark:text-gray-100">Loading shipment details...</p>
      </div>
    );
  }
  return (
    <div>
      {/* Shipment Information */}
      <div className="grid grid-cols-2 gap-4 text-sm mb-6">
        <div>
          <div className="text-gray-500 dark:text-gray-400 mb-1">Shipment ID</div>
          <div className="font-semibold text-lg text-gray-900 dark:text-gray-100">{shipment.id}</div>
        </div>
        <div>
          <div className="text-gray-500 dark:text-gray-400 mb-1">Campaign</div>
          <div className="font-semibold text-lg text-gray-900 dark:text-gray-100">
            {shipment.campaignId && shipment.campaignName
              ? shipment.campaignName
              : <span className="text-gray-400 dark:text-gray-500 italic">Not assigned</span>}
          </div>
        </div>
        <div>
          <div className="text-gray-500 dark:text-gray-400 mb-1">Organization</div>
          <div className="font-semibold text-lg text-gray-900 dark:text-gray-100">{shipment.organization || 'Not specified'}</div>
        </div>
        <div>
          <div className="text-gray-500 dark:text-gray-400 mb-1">Created At</div>
          <div className="font-semibold text-lg text-gray-900 dark:text-gray-100">{shipment.createdAt ? formatDate(shipment.createdAt) : 'Not specified'}</div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
          <div className="text-gray-500 dark:text-gray-400 text-sm">Donations</div>
          <div className="font-bold text-xl">{shipment.donations || 0}</div>
        </div>
        <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
          <div className="text-gray-500 dark:text-gray-400 text-sm">Amount</div>
          <div className="font-bold text-xl">${shipment.amount || 0}</div>
        </div>
        <div className="bg-gray-50 dark:bg-slate-800 p-4 rounded-lg">
          <div className="text-gray-500 dark:text-gray-400 text-sm">Bars Donated</div>
          <div className="font-bold text-xl">{shipment.donatedBars || 0}</div>
        </div>
      </div>

      {/* Ordered Items Section */}
      <div className="mb-6">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Ordered</h3>
        <table className="min-w-full border dark:border-white/10 rounded-lg">
          <thead className="bg-gray-50 dark:bg-slate-800">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Item</th>
              <th className="px-4 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Quantity</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t dark:border-white/10">
              <td className="px-4 py-2">{shipment.campaignName || "UK Homeless (Porch)"}</td>
              <td className="px-4 py-2">{shipment.orderedBoxes || 0} boxes</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Fulfilled Items Section */}
      <div className="mb-6">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Fulfilled</h3>
        <table className="min-w-full border dark:border-white/10 rounded-lg">
          <thead className="bg-gray-50 dark:bg-slate-800">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Message ID</th>
              <th className="px-4 py-2 text-left font-medium text-gray-500 dark:text-gray-400">Boxes</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t dark:border-white/10">
              <td className="px-4 py-2">
                {shipment.logisticsDetails?.includes('http') ? (
                  <a
                    href={shipment.logisticsDetails}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {shipment.logisticsDetails}
                  </a>
                ) : 'N/A'}
              </td>
              <td className="px-4 py-2">{shipment.fulfilledBoxes || 0} boxes</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Logistics Details */}
      <div className="mb-6">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Logistics Details</h3>
        <div className="border dark:border-white/10 rounded-lg p-4 bg-gray-50 dark:bg-slate-800">
          {shipment.logisticsDetails || "No logistics details available"}
        </div>
        {shipment.logisticsDetails?.includes('http') && (
          <Button
            variant="primary"
            size="md"
            className="mt-2"
          >
            Shipment Tracker
          </Button>
        )}
      </div>

      {/* Map Section with LocationMap component */}
      <div className="mb-6">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Location</h3>
        <LocationMap
          latitude={shipment.latitude}
          longitude={shipment.longitude}
          address={shipment.address}
        />
      </div>
    </div>
  );
};

// Component for edit form with validation
const ShipmentDetailsEditForm: React.FC<{
  shipment: Partial<ShipmentDetail>;
  onChange: (shipment: Partial<ShipmentDetail>) => void;
  campaigns?: Campaign[];
  loadingCampaigns?: boolean;
  campaignError?: string | null;
}> = ({ shipment, onChange, campaigns = [], loadingCampaigns = false, campaignError = null }) => {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Shipment ID
        </label>
        <input
          type="text"
          className="w-full p-2 border border-gray-300 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 rounded-md"
          value={shipment.id || ''}
          onChange={(e) => onChange({
            ...shipment,
            id: parseInt(e.target.value) || 0
          })}
          disabled
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">ID cannot be changed</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Name
        </label>
        <input
          type="text"
          className="w-full p-2 border border-gray-300 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 rounded-md"
          value={shipment.name || ''}
          onChange={(e) => onChange({
            ...shipment,
            name: e.target.value
          })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Campaign
        </label>
        <select
          className="w-full p-2 border border-gray-300 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 rounded-md"
          value={shipment.campaignId || ''}
          onChange={(e) => {
            const campaignId = e.target.value ? parseInt(e.target.value) : undefined;
            const campaignName = campaignId
              ? campaigns.find(c => c.id === campaignId)?.name || ''
              : '';

            onChange({
              ...shipment,
              campaignId,
              campaignName
            });
          }}
          disabled={loadingCampaigns}
        >
          <option value="">-- Select Campaign --</option>
          {campaigns.map(campaign => (
            <option key={campaign.id} value={campaign.id}>
              {campaign.name}
            </option>
          ))}
        </select>
        {loadingCampaigns && (
          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">Loading campaigns...</div>
        )}
        {campaignError && (
          <div className="text-sm text-red-500 dark:text-red-400 mt-1">{campaignError}</div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Status
        </label>
        <select
          className="w-full p-2 border border-gray-300 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 rounded-md"
          value={shipment.status || ''}
          onChange={(e) => onChange({
            ...shipment,
            status: e.target.value as ShipmentStatus
          })}
        >
          {Object.values(ShipmentStatus).map(status => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Ordered Boxes
        </label>
        <input
          type="number"
          className="w-full p-2 border border-gray-300 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 rounded-md"
          value={shipment.orderedBoxes || ''}
          onChange={(e) => onChange({
            ...shipment,
            orderedBoxes: parseInt(e.target.value) || 0
          })}
          min={1}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Organization
        </label>
        <input
          type="text"
          className="w-full p-2 border border-gray-300 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 rounded-md"
          value={shipment.organization || ''}
          onChange={(e) => onChange({
            ...shipment,
            organization: e.target.value
          })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Logistics Details
        </label>
        <textarea
          className="w-full p-2 border border-gray-300 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 rounded-md"
          rows={3}
          value={shipment.logisticsDetails || ''}
          onChange={(e) => onChange({
            ...shipment,
            logisticsDetails: e.target.value
          })}
        ></textarea>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Notify Donors
        </label>
        <div className="flex items-center">
          <input
            type="checkbox"
            className="h-4 w-4 text-green-600 border-gray-300 dark:border-white/10 rounded"
            checked={shipment.notifyDonors || false}
            onChange={(e) => onChange({
              ...shipment,
              notifyDonors: e.target.checked
            })}
          />
          <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
            Send notifications to donors when status changes
          </span>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Address
        </label>
        <input
          type="text"
          className="w-full p-2 border border-gray-300 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 rounded-md"
          value={shipment.address || ''}
          onChange={(e) => onChange({
            ...shipment,
            address: e.target.value
          })}
        />
        <Button
          variant="secondary"
          size="sm"
          className="mt-1"
        >
          Search
        </Button>
      </div>

      {/* Map with LocationMap component */}
      <LocationMap
        latitude={shipment.latitude}
        longitude={shipment.longitude}
        address={shipment.address}
      />
    </div>
  );
};

// Component for history view
const ShipmentHistoryView: React.FC<{history: ShipmentHistoryItem[]; isLoading?: boolean}> = ({ history, isLoading = false }) => {
  return (
    <div>
      <div className="mb-4">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100">Event History</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Showing all events for this shipment, most recent first
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900 dark:border-gray-100"></div>
        </div>
      ) : history.length > 0 ? (
        <div className="space-y-4">
          {history.map((event) => (
            <div key={event.id} className="border-l-4 border-primary-100 pl-4 py-2">
              <div className="font-semibold text-gray-900 dark:text-gray-100">
                {event.title}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                {formatDate(event.createdDate)}
              </div>
              {event.ordered > 0 && (
                <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  Ordered: {event.ordered}
                </div>
              )}
              {(event.userName || event.userEmail) && (
                <div className="text-sm">
                  {event.userName && <span className="font-semibold text-gray-900 dark:text-gray-100">{event.userName}</span>}
                  {event.userEmail && <span className="text-gray-500 dark:text-gray-400 ml-1">{event.userEmail}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          No history available for this shipment
        </div>
      )}
    </div>
  );
};

export default ShipmentDetailsSideNav;
